import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../i18n/useTranslation';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Alert, type AlertTone } from '../components/atoms/Alert/Alert';
import { Badge } from '../components/atoms/Badge/Badge';
import { Button } from '../components/atoms/Button/Button';
import { Card } from '../components/atoms/Card/Card';
import { Inline } from '../components/atoms/Inline/Inline';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { Stack } from '../components/atoms/Stack/Stack';
import { Textarea } from '../components/atoms/Textarea/Textarea';
import { Heading } from '../components/atoms/Typography/Heading';
import { Text } from '../components/atoms/Typography/Text';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { causeText, eventText, formatDay, incidentTitle, localDay, whenText } from '../components/incidents/incidentText';
import {
  deleteIncident,
  getIncident,
  incidentKey,
  incidentsKey,
  updateIncident,
  type IncidentDetail,
  type IncidentEvent,
} from '../services/incidentService';
import './IncidentDetailPage.css';

const CAUSE_TONE: Record<'high' | 'medium' | 'info', AlertTone> = { high: 'danger', medium: 'warning', info: 'info' };

type Translate = (key: string, opts?: Record<string, unknown>) => unknown;

/** The window's events grouped by local day, oldest first. */
function groupByDay(events: readonly IncidentEvent[]): Array<{ day: string; events: IncidentEvent[] }> {
  const days = new Map<string, IncidentEvent[]>();
  for (const e of events) {
    const day = localDay(e.at);
    days.set(day, [...(days.get(day) ?? []), e]);
  }
  return [...days.entries()].map(([day, list]) => ({ day, events: list }));
}

/** The incident as Markdown — exactly the texts shown on the page. */
export function incidentMarkdown(detail: IncidentDetail, t: Translate, language: string): string {
  const lines = [
    `# ${incidentTitle(t, detail.incident)}`,
    '',
    `${String(t('incidents.detail.anchor'))}: ${formatDay(localDay(detail.incident.anchorAt), language)}`,
  ];
  if (detail.incident.note) lines.push('', detail.incident.note);
  lines.push('', `## ${String(t('incidents.detail.causesTitle'))}`, '', String(t('incidents.detail.causesDisclaimer')), '');
  if (detail.causes.length === 0) lines.push(String(t('incidents.detail.noCauses')));
  for (const c of detail.causes) lines.push(`- ${causeText(t, c, language)} (${whenText(t, c)})`);
  lines.push('', `## ${String(t('incidents.detail.timelineTitle'))}`);
  for (const group of groupByDay(detail.events)) {
    lines.push('', `### ${formatDay(group.day, language)}`, '');
    for (const e of group.events) lines.push(`- ${eventText(t, e, language)}`);
  }
  return `${lines.join('\n')}\n`;
}

function downloadText(filename: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/markdown;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * One incident (Reef Guard #1824): candidate causes found by fixed rules (never
 * claimed as the cause), the ±14-day timeline around the anchor day, a note,
 * close / reopen, Markdown export and delete.
 */
export function IncidentDetailPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  const { aquariumId = '', incidentId = '' } = useParams<{ aquariumId: string; incidentId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [noteDraft, setNoteDraft] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const query = useQuery({
    queryKey: incidentKey(aquariumId, incidentId),
    queryFn: () => getIncident(aquariumId, incidentId),
    enabled: Boolean(aquariumId && incidentId),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: incidentKey(aquariumId, incidentId) });
    void queryClient.invalidateQueries({ queryKey: incidentsKey(aquariumId) });
  };
  const update = useMutation({
    mutationFn: (patch: { note?: string | null; closed?: boolean }) => updateIncident(aquariumId, incidentId, patch),
    onSuccess: (incident, patch) => {
      setNoteDraft(null);
      setAnnouncement(String(t(patch.closed === undefined ? 'incidents.detail.noteSaved' : incident.closed ? 'incidents.detail.closedNow' : 'incidents.detail.reopenedNow')));
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: () => deleteIncident(aquariumId, incidentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: incidentsKey(aquariumId) });
      navigate(`/aquariums/${aquariumId}/incidents`);
    },
  });

  const detail = query.data;
  const anchorDay = detail ? localDay(detail.incident.anchorAt) : '';
  const days = useMemo(() => (detail ? groupByDay(detail.events) : []), [detail]);
  const back = (
    <Link className="incident-detail__back" to={`/aquariums/${aquariumId}/incidents`}>
      {t('incidents.detail.back')}
    </Link>
  );

  let main;
  if (query.isPending) {
    main = (
      <>
        <PageHeader title={t('incidents.detail.loadingTitle')} back={back} />
        <LiveRegion busy><Text color="muted">{t('incidents.loading')}</Text></LiveRegion>
      </>
    );
  } else if (query.isError || !detail) {
    main = (
      <>
        <PageHeader title={t('incidents.detail.loadingTitle')} back={back} />
        <LoadErrorState title={t('incidents.detail.loadError')} onRetry={() => void query.refetch()} retryLabel={t('incidents.retry')} />
      </>
    );
  } else {
    const { incident } = detail;
    const note = noteDraft ?? incident.note ?? '';
    main = (
      <>
        <PageHeader
          title={incidentTitle(t, incident)}
          description={t('incidents.detail.subtitle', { date: formatDay(anchorDay, language), days: 14 })}
          back={back}
          actions={
            <Inline gap="2" wrap>
              <Button
                variant="secondary"
                onClick={() => downloadText(`incident-${anchorDay}.md`, incidentMarkdown(detail, t, language))}
              >
                {t('incidents.detail.export')}
              </Button>
              <Button variant="secondary" onClick={() => update.mutate({ closed: !incident.closed })} disabled={update.isPending}>
                {t(incident.closed ? 'incidents.detail.reopen' : 'incidents.detail.close')}
              </Button>
            </Inline>
          }
        >
          <Badge tone={incident.closed ? 'neutral' : 'warning'}>{t(incident.closed ? 'incidents.status.closed' : 'incidents.status.open')}</Badge>
        </PageHeader>
        <LiveRegion visuallyHidden>{announcement}</LiveRegion>
        {(update.isError || remove.isError) && <Alert>{t('incidents.detail.saveError')}</Alert>}
        <Section title={t('incidents.detail.causesTitle')} description={t('incidents.detail.causesDisclaimer')}>
          {detail.causes.length === 0 ? (
            <Card><Text>{t('incidents.detail.noCauses')}</Text></Card>
          ) : (
            <ul className="incident-detail__list" data-testid="incident-causes">
              {detail.causes.map((cause, index) => (
                <li key={`${cause.rule}-${index}`}>
                  <Alert tone={CAUSE_TONE[cause.severity]} live="off" title={causeText(t, cause, language)} titleLevel={3}>
                    <Text color="secondary">
                      {whenText(t, cause)} · {t(`incidents.causeHelp.${cause.rule}`)}
                    </Text>
                  </Alert>
                </li>
              ))}
            </ul>
          )}
        </Section>
        <Section title={t('incidents.detail.timelineTitle')} description={t('incidents.detail.timelineDescription')}>
          <Stack gap="4">
            {detail.truncated && <Alert tone="info" live="off">{t('incidents.detail.truncated')}</Alert>}
            {days.length === 0 ? (
              <Card><Text>{t('incidents.detail.noEvents')}</Text></Card>
            ) : (
              <ol className="incident-detail__days" data-testid="incident-timeline">
                {days.map((group) => (
                  <li key={group.day} className={group.day === anchorDay ? 'incident-detail__day incident-detail__day--anchor' : 'incident-detail__day'}>
                    <Card>
                      <Stack gap="2">
                        <Inline gap="2" align="center" wrap>
                          <Heading level={3}>{formatDay(group.day, language)}</Heading>
                          {group.day === anchorDay && <Badge tone="danger">{t('incidents.detail.anchorBadge')}</Badge>}
                        </Inline>
                        <ul className="incident-detail__events">
                          {group.events.map((e) => (
                            <li key={e.id} data-type={e.type}>
                              <Badge tone="neutral">{t(`incidents.eventTypes.${e.type}`)}</Badge>{' '}
                              {e.deepLinkPath ? (
                                <Link className="incident-detail__event-link" to={e.deepLinkPath}>{eventText(t, e, language)}</Link>
                              ) : (
                                <Text as="span">{eventText(t, e, language)}</Text>
                              )}
                            </li>
                          ))}
                        </ul>
                      </Stack>
                    </Card>
                  </li>
                ))}
              </ol>
            )}
          </Stack>
        </Section>
        <Section title={t('incidents.detail.noteTitle')}>
          <Card>
            <Stack gap="3">
              <Textarea
                id="incident-detail-note"
                label={t('incidents.detail.noteLabel')}
                value={note}
                onChange={(e) => setNoteDraft(e.target.value)}
                rows={4}
                maxLength={2000}
                disabled={update.isPending}
              />
              <Inline gap="2" wrap>
                <Button
                  variant="secondary"
                  onClick={() => update.mutate({ note: note.trim() || null })}
                  disabled={update.isPending || noteDraft === null}
                >
                  {t('incidents.detail.saveNote')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (window.confirm(String(t('incidents.detail.deleteConfirm')))) remove.mutate();
                  }}
                  disabled={remove.isPending}
                >
                  {t('incidents.detail.delete')}
                </Button>
              </Inline>
            </Stack>
          </Card>
        </Section>
      </>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={<PageContent width="default">{main}</PageContent>}
    />
  );
}

export default IncidentDetailPage;
